import React from "react";
import {
  ThemeProvider,
  CssBaseline,
  Box,
} from "@mui/material";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Import custom components and hooks
import useWordPressTheme from "./hooks/useWordPressTheme";
import { buildApiUrl, getSiteBasePath } from "./utils/urlHelpers";
import LatestPosts from "./pages/LatestPosts";
import HomePage from "./pages/HomePage";
import PostsPage from "./pages/PostsPage";
import SinglePost from "./pages/SinglePost";
import Navbar from "./components/Navbar";

// Import new practice area pages
import RealEstateLaw from "./pages/RealEstateLaw";
import PersonalInjury from "./pages/PersonalInjury";
import OurHistory from "./pages/OurHistory";
import AboutUs from "./pages/AboutUs";
import PracticeAreas from "./pages/PracticeAreas";
import LegalResources from "./pages/LegalResources";
import Glossary from "./pages/Glossary";
import FAQ from "./pages/FAQ";
import PracticeAreasFAQ from "./pages/PracticeAreasFAQ";

import ContactUs from "./pages/ContactUs";
import Footer from "./components/Footer";

function App() {
  // Use the custom WordPress theme hook
  const theme = useWordPressTheme();
  const [posts, setPosts] = React.useState([]);
  const [appReady, setAppReady] = React.useState(false);

  React.useEffect(() => {
    // Check if WordPress data is available
    if (window.wpData) {
      console.log("WordPress data available:", window.wpData);
      setAppReady(true);
    } else {
      console.warn("WordPress data not available, using fallback");
      setAppReady(true);
    }

    // Function to fetch posts with error handling
    const fetchPosts = async () => {
      try {
        // Determine the API URL
        const apiUrl = buildApiUrl('wp/v2/posts?_embed&per_page=6');

        console.log("Fetching posts from:", apiUrl);

        // Try to fetch posts
        const response = await fetch(apiUrl, {
          headers: {
            "X-WP-Nonce": window.wpData?.nonce || "",
            "Content-Type": "application/json",
          },
          credentials: "same-origin",
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setPosts(data);
      } catch (error) {
        console.error("Error fetching posts:", error);
        // Set empty posts array on error
        setPosts([]);
      }
    };

    fetchPosts();
  }, []);

  if (!appReady) {
    return <div>Loading application...</div>;
  }

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router basename={getSiteBasePath()}>
          <Box
            sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
          >
            <Navbar />
            <Box sx={{ flex: "1 0 auto"}}>
              {/* Main content routes */}
              <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/practice-areas" element={<PracticeAreas />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/legal-resources" element={<LegalResources />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/faq" element={<FAQ />} />
              <Route
                path="/practice-areas-faq"
                element={<PracticeAreasFAQ />}
              />

              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/posts" element={<PostsPage />} />
              <Route path="/post/:id" element={<SinglePost />} />
              <Route path="/real-estate-law" element={<RealEstateLaw />} />
              <Route path="/personal-injury" element={<PersonalInjury />} />
              <Route
                path="/corporate-and-commercial"
                element={<RealEstateLaw />}
              />
              <Route
                path="/calgary-law-office-location"
                element={<OurHistory />}
              />
              <Route path="*" element={<HomePage />} />
            </Routes>
            </Box>
            <Footer />
          </Box>
        </Router>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;